const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://livespainvisa.com';

export const getOrganizationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": `${BASE_URL}#organization`,
    "name": "Live Spain Visa",
    "alternateName": "Live Spain",
    "url": BASE_URL,
    "logo": `${BASE_URL}/logo.png`,
    "description": "Legal services for immigration and international mobility to the Kingdom of Spain. Specialized in helping individuals, business owners, entrepreneurs, retirees, and students settle in Spain.",
    "address": [
        {
            "@type": "PostalAddress",
            "addressLocality": "Barcelona",
            "addressRegion": "Catalonia",
            "postalCode": "08015",
            "streetAddress": "Calle Viladomat 114-116, 6-1 escalera B",
            "addressCountry": "ES"
        },
        {
            "@type": "PostalAddress",
            "addressLocality": "Madrid",
            "addressRegion": "Madrid",
            "postalCode": "28045",
            "streetAddress": "Calle General Lacy 21, 1 Derecha",
            "addressCountry": "ES"
        }
    ],
    "contactPoint": [
        {
            "@type": "ContactPoint",
            "telephone": "+34-659-114-553",
            "contactType": "customer service",
            "areaServed": "ES",
            "availableLanguage": ["English", "Spanish"]
        },
        {
            "@type": "ContactPoint",
            "telephone": "+34-676-035-990",
            "contactType": "customer service",
            "areaServed": "ES",
            "availableLanguage": ["English", "Spanish"]
        }
    ],
    "email": "info@livespainvisa.com",
    "founder": [
        {
            "@type": "Person",
            "name": "Victoria Suárez Lemus",
            "jobTitle": "Lawyer",
            "memberOf": {
                "@type": "Organization",
                "name": "Barcelona Bar Association"
            }
        },
        {
            "@type": "Person",
            "name": "Constanza Suárez Lemus",
            "jobTitle": "Lawyer",
            "memberOf": {
                "@type": "Organization",
                "name": "Madrid Bar Association"
            }
        }
    ],
    "areaServed": {
        "@type": "Country",
        "name": "Spain"
    },
    "serviceType": [
        "Immigration Law",
        "Visa Services",
        "Residency Permits",
        "Citizenship Services",
        "Business Immigration",
        "Student Visas"
    ]
});

export const getBarcelonaOfficeSchema = () => ({
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": `${BASE_URL}#barcelona-office`,
    "name": "Live Spain Visa - Barcelona Office",
    "address": {
        "@type": "PostalAddress",
        "addressLocality": "Barcelona",
        "addressRegion": "Catalonia",
        "postalCode": "08015",
        "streetAddress": "Calle Viladomat 114-116, 6-1 escalera B",
        "addressCountry": "ES"
    },
    "telephone": ["+34-659-114-553", "+34-669-017-348"],
    "geo": {
        "@type": "GeoCoordinates",
        "latitude": "41.380146",
        "longitude": "2.154383"
    }
});

export const getMadridOfficeSchema = () => ({
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": `${BASE_URL}#madrid-office`,
    "name": "Live Spain Visa - Madrid Office",
    "address": {
        "@type": "PostalAddress",
        "addressLocality": "Madrid",
        "addressRegion": "Madrid",
        "postalCode": "28045",
        "streetAddress": "Calle General Lacy 21, 1 Derecha",
        "addressCountry": "ES"
    },
    "telephone": "+34-676-035-990",
    "geo": {
        "@type": "GeoCoordinates",
        "latitude": "40.401872",
        "longitude": "-3.693148"
    }
});

export const getWebsiteSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BASE_URL}#website`,
    "url": BASE_URL,
    "name": "Live Spain Visa",
    "description": "Legal services for immigration and international mobility to Spain. Expert guidance for visas, residency, citizenship, and business immigration.",
    "inLanguage": ["en", "es"],
    "potentialAction": {
        "@type": "SearchAction",
        "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${BASE_URL}/?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
    }
});

export const getServiceSchemas = () => [
    {
        "@context": "https://schema.org",
        "@type": "LegalService",
        "serviceType": "Work Visa and Residency",
        "name": "Live and Work in Spain - Work Visa Lawyer",
        "description": "Expert legal services for work visas and residency in Spain. Services include: Digital Nomad Visa, Non-Lucrative Residency, work permits, highly skilled professional visas, and family member residency. Comprehensive support from visa application to residency permit processing.",
        "provider": {
            "@id": `${BASE_URL}#organization`
        },
        "areaServed": {
            "@type": "Country",
            "name": "Spain"
        },
        "keywords": "work visa Spain, digital nomad visa Spain, work permit Spain, Spanish work visa, non-lucrative residency Spain, work visa lawyer Spain, Spanish residency permit, work in Spain visa"
    },
    {
        "@context": "https://schema.org",
        "@type": "LegalService",
        "serviceType": "Business Immigration",
        "name": "Start a Business in Spain - Business Visa Lawyer",
        "description": "Expert legal services for starting a business in Spain. Guidance for entrepreneurs, startups, and business owners. Services include: business visa applications, company formation, intra-company transfer visas, and residency authorization for business activities.",
        "provider": {
            "@id": `${BASE_URL}#organization`
        },
        "areaServed": {
            "@type": "Country",
            "name": "Spain"
        },
        "keywords": "start business Spain, business visa Spain, entrepreneur visa Spain, startup visa Spain, business immigration Spain, company formation Spain, business visa lawyer Spain"
    },
    {
        "@context": "https://schema.org",
        "@type": "LegalService",
        "serviceType": "Student Visa",
        "name": "Study in Spain - Student Visa Lawyer",
        "description": "Expert legal services for student visas in Spain. Assistance with student visa applications, study permits, internship authorization, and transitioning from student visa to work visa and residency. Support for undergraduate, postgraduate, and research programs.",
        "provider": {
            "@id": `${BASE_URL}#organization`
        },
        "areaServed": {
            "@type": "Country",
            "name": "Spain"
        },
        "keywords": "study visa Spain, student visa Spain, study in Spain visa, student visa lawyer Spain, study permit Spain, Spain student visa, study abroad Spain visa"
    },
    {
        "@context": "https://schema.org",
        "@type": "LegalService",
        "serviceType": "Retirement Visa",
        "name": "Retire in Spain - Retirement Visa Lawyer",
        "description": "Expert legal services for retiring in Spain. Assistance with retirement visa applications, non-lucrative residency for retirees, healthcare access, and residency permits for retirees. Comprehensive support for a peaceful retirement in Spain.",
        "provider": {
            "@id": `${BASE_URL}#organization`
        },
        "areaServed": {
            "@type": "Country",
            "name": "Spain"
        },
        "keywords": "retire in Spain, retirement visa Spain, retire Spain visa, retirement visa lawyer Spain, retire to Spain, Spain retirement visa, non-lucrative visa Spain"
    },
    {
        "@context": "https://schema.org",
        "@type": "LegalService",
        "serviceType": "Citizenship Services",
        "name": "Obtain Spanish Citizenship - Citizenship Lawyer",
        "description": "Expert legal services for obtaining Spanish citizenship. Services for Latin American residents (2 years), spouses of Spanish citizens (1 year), and those with Spanish ancestry. Comprehensive support throughout the citizenship application process.",
        "provider": {
            "@id": `${BASE_URL}#organization`
        },
        "areaServed": {
            "@type": "Country",
            "name": "Spain"
        },
        "keywords": "Spanish citizenship, Spanish citizenship lawyer, obtain Spanish citizenship, Spanish nationality, citizenship Spain, Spanish citizenship application, citizenship lawyer Spain"
    },
    {
        "@context": "https://schema.org",
        "@type": "LegalService",
        "serviceType": "Regularisation Services",
        "name": "Regularisation and Arraigo - Immigration Lawyer",
        "description": "Expert legal services for regularisation and arraigo in Spain. Assistance with social roots (arraigo social), labour roots (arraigo laboral), family roots (arraigo familiar), and training-based residence (arraigo por formación). Help obtaining legal residence for those in irregular situations.",
        "provider": {
            "@id": `${BASE_URL}#organization`
        },
        "areaServed": {
            "@type": "Country",
            "name": "Spain"
        },
        "keywords": "regularisation Spain, arraigo social Spain, arraigo laboral Spain, arraigo familiar Spain, regularisation lawyer Spain, arraigo Spain, regularisation process Spain, irregular immigration lawyer Spain"
    },
    {
        "@context": "https://schema.org",
        "@type": "LegalService",
        "serviceType": "Extraordinary Regularisation",
        "name": "Extraordinary Regularisation Spain 2026 - Legal Services",
        "description": "Expert legal services for extraordinary regularisation in Spain 2026. One-off extraordinary regularisation process via Royal Decree for foreign nationals already in Spain in an irregular situation. We provide pre-screening, evidence-of-presence preparation, and full application support. Application window: April-June 2026.",
        "provider": {
            "@id": `${BASE_URL}#organization`
        },
        "areaServed": {
            "@type": "Country",
            "name": "Spain"
        },
        "keywords": "extraordinary regularisation Spain, extraordinary regulations Spain, regularisation lawyer Spain, Royal Decree regularisation 2026, irregular immigration lawyer Spain, regularisation process Spain, extraordinary regularisation 2026",
        "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/LimitedAvailability",
            "validFrom": "2026-04-01",
            "validThrough": "2026-06-30"
        }
    }
];

export const getBreadcrumbSchema = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url
    }))
});
